import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  fetchAgencies,
  fetchAgencyMemberships,
  fetchRegistrationsForUser,
  fetchClassById,
} from "../../api/apiClient";
import { canPickAgency, defaultAgencyId } from "./agencySelection";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";
import BulkRegistrationWizard from "../../components/BulkRegistrationWizard";

export default function AgencyClassEnrollments() {
  const { user, isFeatureEnabled } = useTenant();
  const { tenantId: activeTenantId } = useActiveTenant();
  const navigate = useNavigate();

  const [approvedMemberships, setApprovedMemberships] = useState([]);
  const [members, setMembers] = useState([]);
  const [classesByUser, setClassesByUser] = useState({});
  const [pastClassesByUser, setPastClassesByUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [bulkWizardOpen, setBulkWizardOpen] = useState(false);
  const [preselectedMembers, setPreselectedMembers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);

  const agencyManagementEnabled = isFeatureEnabled("agencyManagement");

  // Fetch agencies on mount
  useEffect(() => {
    if (!activeTenantId) return;
    fetchAgencies(activeTenantId).then((list) => {
      const arr = Array.isArray(list) ? list : list?.content || [];
      setAgencies(arr);
      const initial = defaultAgencyId(arr, user);
      if (initial) {
        setSelectedAgencyId(initial);
      } else {
        // No agencies found - ensure loading is set to false
        setLoading(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const reload = useCallback(async () => {
    if (!activeTenantId || !selectedAgencyId) return;
    try {
      setLoading(true);
      const mems = await fetchAgencyMemberships(
        activeTenantId,
        selectedAgencyId,
      );
      const approved = mems.filter((m) => m.status === "APPROVED" && m.user);
      setApprovedMemberships(approved);
      setMembers(approved.map((m) => m.user));

      // split each member's classes into current/upcoming and past
      const classesMap = {};
      const pastMap = {};
      const now = new Date();
      await Promise.all(
        approved.map(async (m) => {
          const uid = m.user.id;
          const regs = await fetchRegistrationsForUser(uid, activeTenantId);
          const upcoming = [];
          const past = [];
          for (const r of regs) {
            const cls = await fetchClassById(r.classId);
            if (!cls) continue;
            const isFutureOrCurrent = (() => {
              if (cls.schedule && cls.schedule.length) {
                return cls.schedule.some((s) => {
                  const end = s.end ? new Date(s.end) : new Date(s.start);
                  return end >= now;
                });
              }
              return new Date(cls.date) >= now;
            })();
            if (isFutureOrCurrent) upcoming.push(cls);
            else past.push({ ...cls, registrationStatus: r.status });
          }
          classesMap[uid] = upcoming;
          pastMap[uid] = past;
        }),
      );
      setClassesByUser(classesMap);
      setPastClassesByUser(pastMap);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, selectedAgencyId]);

  useEffect(() => {
    if (selectedAgencyId) {
      reload();
    }
  }, [selectedAgencyId, reload]);

  if (!agencyManagementEnabled) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="info">
              Agency management is not enabled for your tenant.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (
    !user ||
    !user.roles?.some((r) =>
      ["AGENCY_ADMIN", "GLOBAL_ADMIN", "ADMIN"].includes(r),
    )
  ) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error">
              You do not have permission to view this page.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!activeTenantId) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <NoTenantSelected message="Select a training center from the top bar to view class enrollments." />
      </Container>
    );
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          mb: 3,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/agency")}
            sx={{
              color: "text.secondary",
              mb: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Agency Management
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}>
            Class Enrollments
          </Typography>
          <Typography color="text.secondary">
            View your agency members' class enrollments and register members for
            classes
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {canPickAgency(user) && (
          <>
            {agencies.length > 1 && (
              <FormControl size="small" sx={{ mb: 3, minWidth: 260 }}>
                <InputLabel>Agency</InputLabel>
                <Select
                  value={selectedAgencyId || ""}
                  label="Agency"
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                >
                  {agencies.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {agencies.length === 1 && (
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
                {agencies[0].name}
              </Typography>
            )}
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            disabled={members.length === 0 || loading}
            onClick={() => {
              setPreselectedMembers([]);
              setBulkWizardOpen(true);
            }}
          >
            Register Members for Class
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : members.length === 0 ? (
          <Typography color="text.secondary">
            No approved members yet. Approve pending requests or invite members
            to get started.
          </Typography>
        ) : (
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": { fontWeight: "bold", bgcolor: "action.hover" },
                  }}
                >
                  <TableCell>Member</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Currently Enrolled In</TableCell>
                  <TableCell>Past Classes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => {
                  const membership = approvedMemberships.find(
                    (am) => am.user?.id === m.id,
                  );
                  const userClasses = classesByUser[m.id] || [];
                  const pastClasses = pastClassesByUser[m.id] || [];
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {m.fullName || m.name || m.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {m.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {membership?.category || (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {userClasses.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {userClasses.map((c) => (
                              <Chip
                                key={c.id}
                                label={c.title}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None enrolled
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell data-testid={`past-classes-${m.id}`}>
                        {pastClasses.length > 0 ? (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {pastClasses.map((c) => (
                              <Chip
                                key={c.id}
                                label={c.title}
                                size="small"
                                title={c.registrationStatus || undefined}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setPreselectedMembers([
                              {
                                ...m,
                                category: membership?.category,
                              },
                            ]);
                            setBulkWizardOpen(true);
                          }}
                        >
                          Register
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <BulkRegistrationWizard
          open={bulkWizardOpen}
          onClose={() => setBulkWizardOpen(false)}
          onComplete={() => {
            setBulkWizardOpen(false);
            reload();
          }}
          members={members.map((m) => {
            const membership = approvedMemberships.find(
              (am) => am.user?.id === m.id,
            );
            return { ...m, category: membership?.category };
          })}
          preselectedMembers={preselectedMembers}
          agencyId={selectedAgencyId}
          tenantId={activeTenantId}
        />
      </Container>
    </>
  );
}
